import { GraduationCap, Pencil, Plus } from 'lucide-react'
import { useLocale } from '../i18n/context'

interface Props {
  /** Launches Learn for this deck. */
  onStudy: () => void
  /**
   * The deck's write actions — create a card, enter editing mode (ADR-067
   * Decision 2; the bar is that mode's one entrance). Offered together or not
   * at all: absent on a deck the reader cannot write to, where the bar carries
   * Study alone (brief §4.3 — search lives in the pinned top strip) and nothing
   * is rendered disabled. The caller decides whether to pass it, once, from the
   * deck's ownership; the bar never infers writability from a missing handler.
   */
  write?: {
    onAddCard: () => void
    onEnterEditing: () => void
  }
}

/**
 * The deck screen's pinned bottom bar in viewing mode (ADR-067 Decision 1,
 * brief §9).
 *
 * Renders the bar's own body only. Positioning is the caller's (DeckMobileFeed)
 * — it owns the single viewport measurement that every pinned element on this
 * screen shares, and this component must not acquire a second one.
 *
 * Geometry per brief §9: 44 px targets in a 60 px band, with the home-indicator
 * inset painted in the bar's own colour rather than left as a gap. The height
 * that results is never written down as a constant anywhere — the feed measures
 * this element (ADR-067 forbids a hand-tuned constant for it).
 */
export default function DeckViewBar({ onStudy, write }: Props) {
  const { tr } = useLocale()
  const dd = tr.deckDetail

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px calc(8px + env(safe-area-inset-bottom, 0px))',
        // Chrome plane + hairline together (DESIGN_SYSTEM.md Wave 4 §7). The
        // hairline is not decoration: in light theme --bg-chrome equals the card
        // surface exactly, so background alone gives the bar no edge. The shadow
        // is cast away from the feed, which is why it is a distinct token from
        // --shadow-float rather than a reused one.
        background: 'var(--bg-chrome)',
        borderTop: '1px solid var(--border-chrome)',
        boxShadow: 'var(--shadow-chrome-above)',
      }}
    >
      {/* The one solid control on this plane, taking the far end of its ramp
          (--control-solid / --control-solid-text). Not --accent: on the
          multi-select bar accent means "selected", and a filled accent capsule
          here would read as a selection state rather than the primary action. */}
      <button
        onClick={onStudy}
        style={{
          flex: 1, minWidth: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
          height: 44, padding: '0 18px',
          background: 'var(--control-solid)', color: 'var(--control-solid-text)',
          border: 'none', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
          fontFamily: 'var(--font-ui)', fontSize: 14.5, fontWeight: 600,
        }}
      >
        <GraduationCap size={18} strokeWidth={1.9} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {dd.barStudy}
        </span>
      </button>

      {write && (
        <>
          <button
            onClick={write.onAddCard}
            aria-label={dd.barAddCardAria}
            style={secondaryControlStyle}
          >
            <Plus size={18} strokeWidth={2} />
            <span>{dd.barAddCard}</span>
          </button>

          <button
            onClick={write.onEnterEditing}
            style={secondaryControlStyle}
          >
            <Pencil size={17} strokeWidth={1.9} />
            <span>{dd.barEdit}</span>
          </button>
        </>
      )}
    </div>
  )
}

const secondaryControlStyle = {
  flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 44, padding: '0 14px',
  background: 'transparent',
  border: '1px solid var(--border-chrome)',
  borderRadius: 'var(--radius-pill)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600,
} as const
